import { useEffect, useState } from "react";
import type { AgentDTO, ApiClientDTO } from "../../../shared/types";
import { List, ListRow, listRowCls } from "../../components/List";
import { confirmDialog, Dialog } from "../../components/Dialog";
import { TextLink } from "../../components/TextLink";
import { Badge, Button, ErrorText, Field, inputCls, PageHeader, timeAgo } from "../../components/ui";
import { del, get, patch, post } from "../../lib/api";
import { Switch } from "../../components/Switch";

type Grant = { agentId: string; scopePattern: string };
type Issued = { client: ApiClientDTO; key: string };

/** The key, the one time it is shown. */
function KeyBox({ issued }: { issued: Issued }) {
  const [copied, setCopied] = useState(false);
  const agent = issued.client.agents[0]?.agentSlug ?? "<agent-slug>";
  return (
    <div className="space-y-3">
      <p className="text-sm text-muted">
        Copy the key for <b>{issued.client.name}</b> now. It is stored hashed and never shown again; rotate it if it is lost.
      </p>
      <div className="rounded-md border border-line bg-fill-subtle p-3">
        <div className="mb-1 flex items-center justify-between gap-2">
          <span className="text-xs font-semibold tracking-wide text-muted uppercase">API key</span>
          <TextLink
            className="text-xs"
            onClick={async () => {
              await navigator.clipboard.writeText(issued.key);
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            }}
          >
            {copied ? "Copied" : "Copy"}
          </TextLink>
        </div>
        <pre className="font-mono text-xs break-all whitespace-pre-wrap">{issued.key}</pre>
      </div>
      <pre className="overflow-auto rounded-md bg-fill p-3 font-mono text-tiny text-muted">
        {`curl -X POST ${location.origin}/api/v1/runs \\\n  -H "Authorization: Bearer <key>" -H "Content-Type: application/json" \\\n  -d '{"agent":"${agent}","scope":"account:acc_123","prompt":"…"}'\n\ncurl ${location.origin}/api/v1/runs/<runId> -H "Authorization: Bearer <key>"`}
      </pre>
    </div>
  );
}

function GrantsEditor({ agents, grants, onChange }: { agents: AgentDTO[]; grants: Grant[]; onChange: (grants: Grant[]) => void }) {
  return (
    <Field label="Agents it may wake" hint="Per agent, which scopes: * (every scope), account:* (one kind of key) or one exact key such as account:acc_123.">
      <List>
        {agents.map((a) => {
          const grant = grants.find((g) => g.agentId === a.id);
          return (
            <label key={a.id} className={`flex items-center gap-3 ${listRowCls} text-sm`}>
              <input
                type="checkbox"
                checked={Boolean(grant)}
                onChange={(e) => onChange(e.target.checked ? [...grants, { agentId: a.id, scopePattern: "*" }] : grants.filter((g) => g.agentId !== a.id))}
              />
              <span className="min-w-0 flex-1 truncate">
                {a.name} <span className="font-mono text-xs text-muted">{a.slug}</span>
                {!a.enabled && <Badge tone="amber">disabled</Badge>}
              </span>
              {grant && (
                <span className="w-44 shrink-0">
                  <input
                    className={`${inputCls} font-mono text-xs`}
                    value={grant.scopePattern}
                    placeholder="*"
                    onChange={(e) => onChange(grants.map((g) => (g.agentId === a.id ? { ...g, scopePattern: e.target.value } : g)))}
                  />
                </span>
              )}
            </label>
          );
        })}
      </List>
    </Field>
  );
}

function ClientForm({ client, agents, onSaved, onIssued }: { client: ApiClientDTO | null; agents: AgentDTO[]; onSaved: () => Promise<void>; onIssued: (issued: Issued) => void }) {
  const [name, setName] = useState(client?.name ?? "");
  const [grants, setGrants] = useState<Grant[]>(client?.agents.map((a) => ({ agentId: a.agentId, scopePattern: a.scopePattern })) ?? []);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      if (client) await patch(`/api/api-clients/${client.id}`, { name, agents: grants });
      else onIssued(await post<Issued>("/api/api-clients", { name, agents: grants }));
      await onSaved();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-4">
      <Field label="Name" hint="The system that holds the key, e.g. salesos. Shown on every run it wakes.">
        <input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} autoComplete="off" />
      </Field>
      <GrantsEditor agents={agents} grants={grants} onChange={setGrants} />
      <ErrorText error={error} />
      <div className="flex justify-end">
        <Button variant="primary" disabled={busy || !name.trim()} onClick={save}>
          {busy ? "Saving…" : client ? "Save" : "Create and show key"}
        </Button>
      </div>
    </div>
  );
}

export function AdminApiClients() {
  const [clients, setClients] = useState<ApiClientDTO[]>([]);
  const [agents, setAgents] = useState<AgentDTO[]>([]);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<ApiClientDTO | null>(null);
  const [issued, setIssued] = useState<Issued | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = () =>
    get<ApiClientDTO[]>("/api/api-clients").then((list) => {
      setClients(list);
      setEditing((cur) => (cur ? list.find((c) => c.id === cur.id) ?? null : cur));
    }, () => {});
  useEffect(() => {
    load();
    get<AgentDTO[]>("/api/agents").then(setAgents, () => {});
  }, []);

  const run = async (fn: () => Promise<void>) => {
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return (
    <div>
      <PageHeader
        title="API clients"
        subtitle="Outside systems that wake agents through POST /api/v1/runs. Each key says which agents it may wake and in which scopes; the key is shown once."
        actions={<Button variant="primary" onClick={() => setCreating(true)}>Add API client</Button>}
      />
      <div className="p-4 md:p-6">
        <List
          empty={
            <>
              No API clients yet. <TextLink onClick={() => setCreating(true)}>Add the first one</TextLink>.
            </>
          }
        >
          {clients.map((c) => (
            <ListRow
              key={c.id}
              title={
                <>
                  <span className="truncate font-medium">{c.name}</span>
                  <span className="font-mono text-xs text-muted">pak_{c.keyPrefix}…</span>
                  {!c.enabled && <Badge tone="amber">disabled</Badge>}
                </>
              }
              subtitle={
                <span title={c.agents.map((a) => `${a.agentSlug} (${a.scopePattern})`).join(", ")}>
                  {c.agents.length ? c.agents.map((a) => `${a.agentName} (${a.scopePattern})`).join(" · ") : "May wake no agent yet"}
                </span>
              }
              trailing={
                <>
                  <span className="whitespace-nowrap">{c.lastUsedAt ? `Used ${timeAgo(c.lastUsedAt)}` : "Never used"}</span>
                  <Button onClick={() => setEditing(c)}>Manage</Button>
                </>
              }
            />
          ))}
        </List>
      </div>

      <Dialog open={creating} onClose={() => setCreating(false)} size="lg" title="Add API client">
        <ClientForm
          client={null}
          agents={agents}
          onSaved={load}
          onIssued={(i) => {
            setCreating(false);
            setIssued(i);
          }}
        />
      </Dialog>

      <Dialog
        open={Boolean(editing)}
        onClose={() => setEditing(null)}
        size="lg"
        title={editing?.name ?? ""}
        headerAction={
          editing && (
            <Switch
              aria-label={`${editing.name} on`}
              checked={editing.enabled}
              onChange={(enabled) => run(async () => { await patch(`/api/api-clients/${editing.id}`, { enabled }); await load(); })}
            />
          )
        }
        secondary={
          editing && (
            <>
              <Button
                variant="danger"
                onClick={() =>
                  run(async () => {
                    if (!(await confirmDialog({ title: `Delete ${editing.name}?`, body: "Its key stops working immediately.", confirm: "Delete client", danger: true }))) return;
                    await del(`/api/api-clients/${editing.id}`);
                    setEditing(null);
                    await load();
                  })
                }
              >
                Delete
              </Button>
              <Button
                onClick={() =>
                  run(async () => {
                    if (!(await confirmDialog({ title: `Rotate the key of ${editing.name}?`, body: "The current key stops working immediately; the new one is shown once.", confirm: "Rotate key", danger: true }))) return;
                    setIssued(await post<Issued>(`/api/api-clients/${editing.id}/rotate-key`));
                    setEditing(null);
                    await load();
                  })
                }
              >
                Rotate key
              </Button>
            </>
          )
        }
      >
        {editing && (
          <div className="space-y-3">
            {!editing.enabled && <p className="rounded-xl bg-warning/10 px-3 py-2 text-sm text-warning">Off: this client's key is refused. Everything here stays editable.</p>}
            <ClientForm key={editing.updatedAt} client={editing} agents={agents} onSaved={load} onIssued={() => {}} />
            <ErrorText error={error} />
          </div>
        )}
      </Dialog>

      <Dialog open={Boolean(issued)} onClose={() => setIssued(null)} title="Copy the key now" primary={<Button variant="primary" onClick={() => setIssued(null)}>Done</Button>} cancel={false}>
        {issued && <KeyBox issued={issued} />}
      </Dialog>
    </div>
  );
}
